import React, { useState } from 'react'
import { Source_Serif_4 } from 'next/font/google'
import {
    MessagePrimitive,
    ThreadPrimitive,
    groupPartByType,
    type MessageState,
} from '@assistant-ui/react'
import { useRouter } from 'next/router'
import { AlertCircle, ChevronRight, CircleCheck, Sparkle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { AI_CREATE_STREAM_NODE } from '@storefronts/common-types'
import type { AdPlatform, MetaAd } from '@storefronts/common-types'
import { AD_PLATFORM } from '@storefronts/common-types'
import { cn } from '@/lib/utils'
import { AnimatedShinyText } from '@/components/magicui/AnimatedShinyText'
import { AgentThinkingMark } from '../agent-chat/AgentThinkingMark'
import { AgentTurnActivity } from '../agent-chat/AgentTurnActivity'
import { MarkdownText } from '../agent-chat/MarkdownText'
import {
    getReasoningBodyAfterHeadline,
    getReasoningHeadline,
    timelineFromAssistantParts,
} from '../agent-chat/agentChatUtils'
import { useAIChatContext, type PrefaceContext } from './AIChatContext'
import { useAiCreateRunSignals } from './AiCreateRunSignals'
import { getNodeLabel, getNodeRunningLabel } from './nodeDisplay'

const serifFont = Source_Serif_4({ subsets: ['latin'], weight: ['400'] })

const activityTextClassName = 'font-sans text-[13px] text-muted-foreground'

const assistantTextClassName = cn(
    serifFont.className,
    'text-[15px] leading-relaxed text-foreground/90',
    // Markdown image thumbnails (curated media notes) render inline and small.
    '[&_img]:mr-2 [&_img]:inline-block [&_img]:size-20 [&_img]:rounded-lg [&_img]:border [&_img]:border-border [&_img]:object-cover [&_img]:align-middle',
)

// Only reasoning collapses into a thought block; node lines stay visible in
// the flow, so each node reads as a step with its thinking tucked under it.
const assistantPartGroupBy = groupPartByType({
    reasoning: ['group-thought'],
})

/** TOOL_CALL_RESULT.content is the bridge's JSON payload; the aggregator
 * pre-parses valid JSON, so the result arrives as an object (or the raw
 * string when parsing failed). Anything else renders without a summary. */
function parseNodeResult(result: unknown): {
    summary?: string
    isError?: boolean
    skipped?: boolean
    data?: Record<string, unknown>
} {
    let payload: unknown = result
    if (typeof result === 'string') {
        try {
            payload = JSON.parse(result)
        } catch {
            return {}
        }
    }
    if (!payload || typeof payload !== 'object') {
        return {}
    }
    const { summary, isError, skipped, data } = payload as {
        summary?: unknown
        isError?: unknown
        skipped?: unknown
        data?: unknown
    }
    const hasData =
        !!data && typeof data === 'object' && Object.keys(data).length > 0
    return {
        summary: typeof summary === 'string' ? summary : undefined,
        isError: isError === true,
        skipped: skipped === true,
        data: hasData ? (data as Record<string, unknown>) : undefined,
    }
}

const DETAIL_IMAGE_KEYS = new Set([
    'screenshotUrl',
    'postScreenshotUrl',
    'thumbnailUrl',
])

function humanizeDetailKey(key: string): string {
    const spaced = key.replace(/([A-Z])/g, ' $1').toLowerCase()
    return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function isRecordArray(value: unknown): value is Record<string, unknown>[] {
    return (
        Array.isArray(value) &&
        value.every((entry) => !!entry && typeof entry === 'object')
    )
}

const DetailEntry: React.FC<{ name: string; value: unknown }> = ({
    name,
    value,
}) => {
    if (DETAIL_IMAGE_KEYS.has(name) && typeof value === 'string') {
        return (
            <div>
                <div className="mb-1 text-muted-foreground">
                    {humanizeDetailKey(name)}
                </div>
                <a href={value} target="_blank" rel="noreferrer">
                    <img
                        src={value}
                        alt={humanizeDetailKey(name)}
                        className="h-44 w-32 rounded-lg border object-cover object-top border-border"
                    />
                </a>
            </div>
        )
    }
    if (
        name === 'stills' &&
        Array.isArray(value) &&
        value.every((entry) => typeof entry === 'string')
    ) {
        return (
            <div className="flex flex-wrap gap-2">
                {(value as string[]).map((url) => (
                    <img
                        key={url}
                        src={url}
                        alt="Video still"
                        className="h-28 rounded-lg border object-cover border-border"
                    />
                ))}
            </div>
        )
    }
    if (isRecordArray(value)) {
        return (
            <div>
                <div className="mb-1 text-muted-foreground">
                    {humanizeDetailKey(name)}
                </div>
                <div className="flex flex-col gap-1">
                    {value.map((entry, index) => (
                        <div key={index}>
                            {Object.values(entry)
                                .filter(
                                    (field) =>
                                        field !== null && field !== undefined,
                                )
                                .join(' — ')}
                        </div>
                    ))}
                </div>
            </div>
        )
    }
    if (Array.isArray(value)) {
        return (
            <div>
                <span className="text-muted-foreground">
                    {humanizeDetailKey(name)}:{' '}
                </span>
                {value.join(', ')}
            </div>
        )
    }
    if (typeof value === 'string' && value.length > 80) {
        return (
            <div>
                <div className="mb-1 text-muted-foreground">
                    {humanizeDetailKey(name)}
                </div>
                <div className="whitespace-pre-wrap">{value}</div>
            </div>
        )
    }
    return (
        <div>
            <span className="text-muted-foreground">
                {humanizeDetailKey(name)}:{' '}
            </span>
            {String(value)}
        </div>
    )
}

const NodeDetails: React.FC<{ data: Record<string, unknown> }> = ({ data }) => (
    <div className="text-foreground/90 mt-1.5 flex flex-col gap-2.5 rounded-lg border p-3 text-xs bg-background border-border">
        {Object.entries(data)
            .filter(([, value]) => value !== null && value !== undefined)
            .map(([name, value]) => (
                <DetailEntry key={name} name={name} value={value} />
            ))}
    </div>
)

/** A `group-…` pseudo-part from GroupedParts: `status` runs while any
 * contained part runs, `indices` point into the message's parts array. */
function asThoughtGroup(part: unknown): {
    status: { type: string }
    indices: readonly number[]
} | null {
    if (!part || typeof part !== 'object') {
        return null
    }
    const candidate = part as {
        status?: { type?: unknown }
        indices?: unknown
    }
    return Array.isArray(candidate.indices) &&
        typeof candidate.status?.type === 'string'
        ? (part as { status: { type: string }; indices: readonly number[] })
        : null
}

const PRODUCT_THUMB_LIMIT = 10

/** Selected-product thumbnails, shown once the product node has run. */
function ProductThumbStrip() {
    const { state } = useAIChatContext()
    const products = state.snapshot?.selectedProducts?.products ?? []
    const withImages = products.filter((product) => product.imageUrl)
    if (withImages.length === 0) {
        return null
    }
    const visible = withImages.slice(0, PRODUCT_THUMB_LIMIT)
    const hidden = withImages.length - visible.length
    return (
        <div className="mt-2 flex flex-wrap items-center gap-2">
            {visible.map((product, index) => (
                <img
                    key={index}
                    src={product.imageUrl as string}
                    alt={product.title}
                    title={product.title}
                    className="size-16 rounded-lg border object-cover border-border"
                />
            ))}
            {hidden > 0 && (
                <span className="text-xs text-muted-foreground">
                    +{hidden} more
                </span>
            )}
        </div>
    )
}

const CreateNodeItem: React.FC<{
    toolName: string
    isRunning: boolean
    result: unknown
}> = ({ toolName, isRunning, result }) => {
    const [open, setOpen] = useState(false)
    const { summary, isError, skipped, data } = parseNodeResult(result)
    if (skipped) return null
    if (isRunning) {
        return (
            <div className={activityTextClassName}>
                <AnimatedShinyText className="text-sm" shimmerDuration="3s">
                    {getNodeRunningLabel(toolName)}
                </AnimatedShinyText>
            </div>
        )
    }
    const line = (
        <>
            <span className="text-foreground/80 font-medium">
                {getNodeLabel(toolName)}
            </span>
            {summary ? <span> — {summary}</span> : null}
        </>
    )
    return (
        <div
            className={cn(activityTextClassName, isError && 'text-destructive')}
        >
            {data ? (
                <Collapsible open={open} onOpenChange={setOpen}>
                    <CollapsibleTrigger className="inline-flex max-w-full items-center gap-1 text-left hover:text-foreground">
                        <span>{line}</span>
                        <ChevronRight
                            className={cn(
                                'size-4 shrink-0 opacity-50 transition-transform',
                                open && 'rotate-90',
                            )}
                        />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="overflow-hidden">
                        <NodeDetails data={data} />
                    </CollapsibleContent>
                </Collapsible>
            ) : (
                <div>{line}</div>
            )}
            {toolName === AI_CREATE_STREAM_NODE.RESOLVE_PRODUCTS && !isError ? (
                <ProductThumbStrip />
            ) : null}
        </div>
    )
}

/** Summaries arrive as markdown (`**Headline**` + body); render the headline
 * as a styled line instead of leaking stripped-marker plain text. While the
 * part streams, the headline lives in the shimmer header, so only the body
 * shows here. */
/** Whether the enclosing thought group is still streaming. */
const GroupLiveContext = React.createContext(false)

/** While the group streams, thinking stays compact — Cursor-style: the active
 * part shows only its last few lines through a small masked window, finished
 * parts collapse to their headline. The full text comes back through the
 * "Thought for a moment" toggle once the group completes. */
const AssistantReasoningPart: React.FC<{
    text: string
    isActive: boolean
}> = ({ text, isActive }) => {
    const groupLive = React.useContext(GroupLiveContext)
    const headline = getReasoningHeadline(text)
    const body = getReasoningBodyAfterHeadline(text)
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    if (!headline && !body) {
        return null
    }
    if (isActive) {
        if (!body) {
            return null
        }
        return (
            <li className={cn(activityTextClassName, 'leading-relaxed')}>
                <div className="flex max-h-16 flex-col justify-end overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_45%)]">
                    <div className="whitespace-pre-wrap">{body}</div>
                </div>
            </li>
        )
    }
    if (groupLive) {
        return headline ? (
            <li className={cn(activityTextClassName, 'leading-relaxed')}>
                <div className="text-foreground/70">{headline}</div>
            </li>
        ) : null
    }
    return (
        <li className={cn(activityTextClassName, 'leading-relaxed')}>
            {headline ? (
                <div className="text-foreground/75 font-medium">{headline}</div>
            ) : null}
            {body ? (
                <div className="mt-0.5 whitespace-pre-wrap">{body}</div>
            ) : null}
        </li>
    )
}

const AssistantText: React.FC = () => (
    <div className={assistantTextClassName}>
        <MarkdownText />
    </div>
)

const CreateAssistantMessage: React.FC<{ message: MessageState }> = ({
    message,
}) => {
    const { runActive } = useAiCreateRunSignals()

    return (
        <MessagePrimitive.Root className="flex w-full flex-col gap-2 py-2">
            <MessagePrimitive.GroupedParts
                groupBy={assistantPartGroupBy}
                indicator="empty"
            >
                {({ part, children }) => {
                    switch (part.type) {
                        case 'group-thought':
                        case 'indicator': {
                            // Liveness and the timeline are per group: only
                            // the group containing a running part shimmers;
                            // finished groups collapse to their trigger.
                            const group = asThoughtGroup(part)
                            const groupParts = group
                                ? group.indices
                                      .map((index) => message.content[index])
                                      .filter(
                                          (groupPart) =>
                                              groupPart !== undefined,
                                      )
                                : []
                            const live = group
                                ? runActive && group.status.type === 'running'
                                : runActive && message.isLast
                            return (
                                <AgentTurnActivity
                                    live={live}
                                    timeline={timelineFromAssistantParts(
                                        groupParts,
                                    )}
                                    defaultOpen={false}
                                    className="border-border/60 ml-1 border-l pl-3 font-sans"
                                >
                                    {part.type === 'group-thought' ? (
                                        <GroupLiveContext.Provider value={live}>
                                            <ol className="my-0 flex list-none flex-col gap-2">
                                                {children}
                                            </ol>
                                        </GroupLiveContext.Provider>
                                    ) : undefined}
                                </AgentTurnActivity>
                            )
                        }
                        case 'reasoning':
                            return (
                                <AssistantReasoningPart
                                    text={part.text}
                                    isActive={part.status?.type === 'running'}
                                />
                            )
                        case 'tool-call':
                            return (
                                <CreateNodeItem
                                    toolName={part.toolName}
                                    isRunning={part.status?.type === 'running'}
                                    result={part.result}
                                />
                            )
                        case 'text':
                            return <AssistantText />
                        default:
                            return null
                    }
                }}
            </MessagePrimitive.GroupedParts>
            {!runActive && message.isLast ? (
                <AgentThinkingMark className="self-start text-foreground" />
            ) : null}
        </MessagePrimitive.Root>
    )
}

const CreateUserMessage: React.FC = () => (
    <MessagePrimitive.Root className="flex w-full flex-col items-end py-2">
        <div className="max-w-[80%] whitespace-pre-wrap break-words rounded-2xl border px-4 py-2.5 text-sm bg-background border-chat-bubble-border text-foreground">
            <MessagePrimitive.Parts />
        </div>
    </MessagePrimitive.Root>
)

const CHANNEL_LABELS: Record<string, string> = {
    [AD_PLATFORM.META]: 'Meta',
    [AD_PLATFORM.GOOGLE]: 'Google',
    [AD_PLATFORM.TIKTOK]: 'TikTok',
    [AD_PLATFORM.AXON]: 'Axon',
    [AD_PLATFORM.PINTEREST]: 'Pinterest',
    [AD_PLATFORM.SNAPCHAT]: 'Snapchat',
    [AD_PLATFORM.ROKT]: 'Rokt',
    [AD_PLATFORM.UNSPECIFIED]: 'Other',
}

function channelLabel(platform: AdPlatform): string {
    return CHANNEL_LABELS[platform] ?? platform
}

function prefaceDetailPills(preface: PrefaceContext): string[] {
    const pills: string[] = []
    if (preface.personaName) pills.push(preface.personaName)
    if (preface.productNames?.length) {
        pills.push(
            preface.productNames.length === 1
                ? preface.productNames[0]
                : `${preface.productNames.length} products`,
        )
    }
    if (preface.referenceUrl) pills.push(preface.referenceUrl)
    if (preface.sourceStorefrontName) pills.push(preface.sourceStorefrontName)
    if (preface.variantCount) {
        pills.push(
            `${preface.variantCount} ${preface.variantCount === 1 ? 'variant' : 'variants'}`,
        )
    }
    return pills
}

function PrefaceContextBubble({
    ad,
    preface,
}: {
    ad: MetaAd | null
    preface: PrefaceContext | null
}) {
    if (!preface) return null

    const imageUrl = ad?.thumbnailUrl || ad?.mediaUrl
    const detailPills = prefaceDetailPills(preface)

    return (
        <div className="flex w-full flex-col items-end py-2">
            <div className="max-w-[80%] rounded-2xl border px-4 py-3 bg-background border-chat-bubble-border text-foreground">
                <div className="text-sm">
                    {preface.userInput ||
                        (preface.channel
                            ? `Build a ${channelLabel(preface.channel)} storefront`
                            : 'Build a storefront')}
                </div>
                {(imageUrl || ad?.name) && (
                    <div className="mt-2 flex items-center gap-2.5">
                        {imageUrl && (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                                src={imageUrl}
                                alt={ad?.name ?? ''}
                                className="size-12 rounded-lg border object-cover border-border"
                            />
                        )}
                        {ad?.name && (
                            <span className="max-w-56 truncate text-xs text-muted-foreground">
                                {ad.name}
                            </span>
                        )}
                    </div>
                )}
                {detailPills.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        {detailPills.map((label) => (
                            <span
                                key={label}
                                className="rounded-md border px-1.5 py-0.5 text-xs border-border text-muted-foreground"
                            >
                                {label}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

function EmptyRunState() {
    return (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <Sparkle className="size-6 fill-blackcrow text-blackcrow" />
            <span className={cn(serifFont.className, 'text-sm')}>
                Getting started…
            </span>
        </div>
    )
}

/** Non-interactive composer shell: the build runs on its own. */
function ComposerShell() {
    const { state } = useAIChatContext()
    return (
        <div className="rounded-2xl border px-4 py-3 shadow-[var(--chat-composer-shadow)] bg-background border-border">
            <span className="text-sm text-muted-foreground">
                {state.isComplete
                    ? 'Storefront ready — opening the editor…'
                    : 'Building your storefront…'}
            </span>
        </div>
    )
}

/** End-of-run card: the run redirects shortly after, but the card keeps the
 * destination one click away for anyone reading back the transcript. */
function CompletionCard({ hash }: { hash: string }) {
    const router = useRouter()
    return (
        <div className="mt-3 flex items-center gap-3 rounded-2xl border px-4 py-3 bg-background border-chat-bubble-border">
            <CircleCheck className="size-5 shrink-0 text-blackcrow" />
            <span className="flex-1 text-sm">Your storefront is ready</span>
            <Button
                size="sm"
                variant="blackcrow"
                onClick={() => router.push(`/landingpages/${hash}?tab=0`)}
            >
                Open in editor
            </Button>
        </div>
    )
}

function variantStatusLine(
    variants: { status: string }[] | null | undefined,
): string | null {
    if (!variants || variants.length <= 1) {
        return null
    }
    const done = variants.filter((variant) => variant.status === 'done').length
    return `${done} of ${variants.length} variants ready`
}

export function ChatPanel() {
    const { state } = useAIChatContext()
    const signals = useAiCreateRunSignals()

    const headerStatus = state.isComplete
        ? 'Storefront complete'
        : signals.runFailed
          ? 'Something went wrong'
          : signals.runActive || signals.hasActivity
            ? 'Building your storefront…'
            : 'Ready to help'

    return (
        <ThreadPrimitive.Root
            className={cn(
                serifFont.className,
                'flex h-full min-h-0 flex-col overflow-hidden bg-chat-surface text-foreground',
            )}
        >
            <div className="shrink-0 px-5 pb-1 pt-4">
                <div className="mx-auto flex w-full max-w-3xl items-center gap-2.5">
                    <AgentThinkingMark
                        thinking={signals.runActive}
                        className="text-foreground"
                    />
                    <span className="text-sm text-muted-foreground">
                        {headerStatus}
                    </span>
                    {variantStatusLine(state.snapshot?.variants) ? (
                        <span className="ml-auto text-xs text-muted-foreground">
                            {variantStatusLine(state.snapshot?.variants)}
                        </span>
                    ) : null}
                </div>
            </div>

            {/* Default bottom-follow: pinned while at the bottom, released
                the moment the user scrolls up. Run starts don't yank either —
                only the at-bottom follow moves the viewport. */}
            <ThreadPrimitive.Viewport
                scrollToBottomOnRunStart={false}
                className="min-h-0 flex-1 overflow-y-auto px-5 [overflow-anchor:none]"
            >
                <div className="mx-auto flex w-full max-w-3xl flex-col pb-4 pt-2">
                    <PrefaceContextBubble
                        ad={state.selectedAd}
                        preface={state.prefaceContext}
                    />
                    <ThreadPrimitive.Empty>
                        <EmptyRunState />
                    </ThreadPrimitive.Empty>
                    <ThreadPrimitive.Messages>
                        {({ message }) =>
                            message.role === 'user' ? (
                                <CreateUserMessage />
                            ) : (
                                <CreateAssistantMessage message={message} />
                            )
                        }
                    </ThreadPrimitive.Messages>
                    {/* The page banner owns session-level failures; this one
                        only covers run errors that never reach the snapshot. */}
                    {signals.runFailed &&
                    signals.runErrorMessage &&
                    !state.error ? (
                        <div className="border-destructive/40 bg-destructive/5 mt-2 flex items-start gap-2 rounded-xl border px-3 py-2 text-sm text-destructive">
                            <AlertCircle className="mt-0.5 size-4 shrink-0" />
                            {signals.runErrorMessage}
                        </div>
                    ) : null}
                    {state.isComplete &&
                    state.snapshot?.finalLandingPageHash ? (
                        <CompletionCard
                            hash={state.snapshot.finalLandingPageHash}
                        />
                    ) : null}
                </div>
            </ThreadPrimitive.Viewport>

            <div className="relative shrink-0 px-5 pb-4">
                <div className="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-gradient-to-t from-chat-surface to-transparent" />
                <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
                    <ComposerShell />
                </div>
            </div>
        </ThreadPrimitive.Root>
    )
}
